import { IBidResponse, IAuctionResults } from '../../typings/IPrebid'
import { Bidder } from '../enums'
import { MediaType } from '../enums/MediaType'
import EventManager from '../eventManager/EventManager'
import WebModel from '../models/WebModel'
import { getFloor } from '../prebidFacade/BidRequestAdjustments'
import { Slot } from '../slots/Slot'

const TTL_OVERRIDE_VALUE = 60

export class AuctionHouse {
  private recievedBids: Array<IBidResponse> = []
  private overrideTtls = ['rubicion', 'appnexus', 'sovrn', 'emx']
  readonly ttlOverrideValue = TTL_OVERRIDE_VALUE

  constructor(private model: WebModel) {
    // Add each bidResponse to the bidPool
    EventManager.on(EventManager.events.bidResponse, (bid) => {
      this.addBids([bid])
    })

    return
  }

  /**
   * Adds bid the the bidPool
   * @param bids
   */
  addBids(bids: Array<IBidResponse>): void {
    for (const bid of bids) {
      // No need to add bids of zero to the pool.
      if (bid.cpm >= getFloor(bid.adUnitCode, this.model)) {
        // We suspect that the ttls for the bidders in overrideTtls
        // have their numbers set incorrectly, so we override them here
        if (this.overrideTtls.indexOf(bid.bidder) !== -1) {
          bid.ttl = this.ttlOverrideValue
        }
        bid.url = window.location.href
        this.recievedBids.push(bid)
      }
    }
  }

  getHighestBidData(slot: Slot, currentBid: IAuctionResults): IHighestBidData {
    // Must calculate hb_delta and hb_count before popHighestBidForAdUnit().
    const hbDelta = this.calculateHbDeltaForAdunit(slot)
    const hbCount = this.getHbCountForAdunit(slot)
    const highestBid = this.popHighestBidForAdUnit(slot)

    const currentBidWon =
      highestBid && highestBid.adId
        ? highestBid.adId !==
          (currentBid.targeting && currentBid.targeting.hb_adid)
        : false

    return {
      hbDelta,
      hbCount,
      highestBid,
      currentBidWon
    }
  }

  /**
   * Pass in an adUnitId, and this will find and remove from the bidPool
   * the highest bid that is valid for that adUnit.
   * @param adUnitId
   */
  popHighestBidForAdUnit(slot: Slot): IBidResponse | null {
    const highestBid = this.getHighestBidForAdUnit(slot)
    removeFromArray(this.recievedBids, highestBid)

    return highestBid
  }

  getSize(): number {
    return this.recievedBids.length
  }

  /**
   * hb_delta is a measure of the distance between
   * the highest bid and next highest bid. It can be
   * used to derive whether there is a close competition
   * for an adunit or if a single bidder is bidding
   * much higher than the rest of the pack.
   */
  calculateHbDeltaForAdunit(slot: Slot): number {
    const sortedBids = this.getValidBidsForAdunit(slot).sort(
      (bidA, bidB) => bidB.cpm - bidA.cpm
    )

    if (sortedBids.length === 0) {
      /* eslint-disable-next-line no-magic-numbers */
      return -2
    } else if (sortedBids.length === 1) {
      return -1
    }

    const highBid = sortedBids[0]
    const lowBid = sortedBids[1]

    const delta = Math.floor(((highBid.cpm - lowBid.cpm) / lowBid.cpm) * 100)

    if (delta > 1000) {
      /* eslint-disable-next-line no-magic-numbers */
      return 1001
    }

    return delta
  }

  /**
   * Returns a list of Bidders who have a bid in the pool thats is valid
   * for the passed in adUnit
   * @param adUnitId
   */
  getBiddersForAdUnit(slot: Slot): Array<Bidder> {
    const bidders = {}

    this.getValidBidsForAdunit(slot).forEach((bid) => {
      bidders[bid.bidder] = true
    })

    return Object.keys(bidders) as Array<Bidder>
  }

  /**
   * Calculates the hb_count for a particular adUnit in the pool. 20 is max value returned.
   * @param adUnitId
   */
  getHbCountForAdunit(slot: Slot): number {
    const validBids = this.getValidBidsForAdunit(slot)

    /* eslint-disable-next-line no-magic-numbers */
    const hbCount = validBids.length > 20 ? 20 : validBids.length

    return hbCount
  }

  private getValidBidsForAdunit({ id }: Slot): Array<IBidResponse> {
    this.cleanPool()

    return this.recievedBids.filter((bid) => {
      // Only bids for the current URL are valid
      if (bid.url !== window.location.href) {
        return false
      }

      // Remove the numbers from adunits like `content_3_btf` or
      // the '_refresh' from 'adhesion_desktop_refresh'
      const baseBidAdUnitId = bid.adUnitCode.replace(/_[0-9]+|_refresh/g, '')
      const baseAdUnitId = id.replace(/_[0-9]+|_refresh/g, '')

      // Outstream bids are for 2+ slots only to improve page load speeds
      if (isOutstreamBid(bid)) {
        return bid.adUnitCode === id
      }

      return baseBidAdUnitId === baseAdUnitId
    })
  }

  /**
   * Pass in an adUitId, and this will find the highest bid that is valid
   * @param adUnitId
   */
  private getHighestBidForAdUnit(slot: Slot): IBidResponse | null {
    let highestBid: IBidResponse | null = null
    const validBids = this.getValidBidsForAdunit(slot)
    validBids.forEach((bid) => {
      if (!highestBid) {
        highestBid = bid
      } else {
        if (bid.cpm > highestBid.cpm) {
          highestBid = bid
        }
      }
    })

    return highestBid
  }

  /**
   * Removes bids whose TTL have expired
   */
  private cleanPool(): void {
    const now = Date.now()
    // add a second to cutoff timestamp just top be safe.
    const oneSecondFromNow = now + 1000
    this.recievedBids = this.recievedBids.filter((bid) => {
      return bid.responseTimestamp + bid.ttl * 1000 > oneSecondFromNow
    })
  }
}

function removeFromArray<T>(array: Array<T>, element: T): Array<T> {
  const index = array.indexOf(element)

  if (index !== -1) {
    array.splice(index, 1)
  }

  return array
}

interface IHighestBidData {
  hbDelta: number
  hbCount: number
  highestBid: IBidResponse | null
  currentBidWon: boolean
}

function isOutstreamBid(bid: IBidResponse): boolean {
  // icv
  return (
    bid.bidder === Bidder.gumgum_icv ||
    // unruly
    bid.mediaType === MediaType.video ||
    // triplelift
    !!(
      bid.params &&
      bid.params.inventoryCode &&
      bid.params.inventoryCode.indexOf('video') !== -1
    )
  )
}
